import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PublicLayout } from "@/components/PublicLayout";
import { LOCATIONS, CATEGORY_ORDER, CATEGORY_META } from "@/data/clinic";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  MapPin, Clock, ArrowLeft, ArrowRight, Check, CalendarDays, User, CheckCircle2,
  Loader2, AlertCircle, RotateCw,
} from "lucide-react";

const STEPS = ["Location", "Category", "Treatment", "Date & Time", "Your Details"];

function generateSlots() {
  const slots = [];
  for (let h = 9; h <= 18; h++) {
    slots.push(`${String(h).padStart(2, "0")}:00`);
    if (h !== 18) slots.push(`${String(h).padStart(2, "0")}:30`);
  }
  return slots;
}

function next7Days() {
  const days = [];
  const today = new Date();
  for (let i = 1; i <= 21; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    days.push(d);
  }
  return days;
}

export default function BookingPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [treatments, setTreatments] = useState([]);
  const [loadingTreatments, setLoadingTreatments] = useState(true);
  const [treatmentsError, setTreatmentsError] = useState(false);
  const [step, setStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [location, setLocation] = useState(null);
  const [category, setCategory] = useState(null);
  const [treatment, setTreatment] = useState(null);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });

  const loadTreatments = useCallback(async () => {
    setLoadingTreatments(true);
    setTreatmentsError(false);
    try {
      const res = await api.get("/treatments");
      setTreatments(res.data);
      const pre = params.get("treatment");
      if (pre) {
        const t = res.data.find((x) => x.id === pre);
        if (t) {
          setTreatment(t);
          setCategory(t.category);
        }
      }
    } catch (e) {
      setTreatmentsError(true);
    } finally {
      setLoadingTreatments(false);
    }
  }, [params]);

  useEffect(() => {
    loadTreatments();
  }, [loadTreatments]);

  const slots = useMemo(generateSlots, []);
  const days = useMemo(next7Days, []);

  const categoryTreatments = treatments.filter((t) => t.category === category);

  const canNext = () => {
    if (step === 0) return !!location;
    if (step === 1) return !!category;
    if (step === 2) return !!treatment;
    if (step === 3) return !!date && !!time;
    return false;
  };

  const goNext = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const goBack = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email and phone.");
      return;
    }
    setSubmitting(true);
    try {
        const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
      await api.post("/bookings", {
        ...form,
        location_id: location.id,
        location_name: `${location.name} (${location.country})`,
        treatment_id: treatment.id,
        treatment_name: treatment.name,
        treatment_price: treatment.price,
        treatment_currency: treatment.currency,
        treatment_duration: treatment.duration_minutes,
        category: treatment.category,
        date: dateStr,
        time,
      });
      setSubmitted(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not submit booking.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <PublicLayout>
        <div className="max-w-2xl mx-auto px-5 py-24 text-center">
          <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
            <CheckCircle2 size={72} className="text-brand-green mx-auto" />
            <h1 className="font-serif text-3xl text-brand-green mt-6">Booking Request Received</h1>
            <p className="text-brand-green/70 mt-3">
              Thank you, {form.name}. We've received your request for <b>{treatment.name}</b> at{" "}
              <b>{location.name}</b> on <b>{date.toDateString()}</b> at <b>{time}</b>.
            </p>
            <div className="mt-6 bg-white rounded-2xl border border-brand-green/10 p-5 text-left max-w-md mx-auto space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-brand-green/60">Email</span>
                <span className="text-brand-green font-medium">{form.email}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-brand-green/60">Phone</span>
                <span className="text-brand-green font-medium">{form.phone}</span>
              </div>
            </div>
            <p className="mt-6 text-brand-gold font-medium">
              We will call or email you to confirm your booking.
            </p>
            <div className="mt-8 flex gap-4 justify-center">
              <button
                onClick={() => navigate("/")}
                data-testid="confirm-home-button"
                className="bg-brand-green text-white rounded-full px-8 py-3 font-medium hover:bg-brand-greenDark transition-all"
              >
                Back to Home
              </button>
            </div>
          </motion.div>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-14">
        <h1 className="font-serif text-3xl sm:text-4xl text-brand-green text-center">Book Your Appointment</h1>

        {/* Stepper */}
        <div className="flex items-center justify-center gap-2 sm:gap-3 mt-8 mb-12 flex-wrap">
          {STEPS.map((label, i) => (
            <div key={label} className="flex items-center gap-2 sm:gap-3">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm transition-all ${
                  i < step
                    ? "bg-brand-green text-white"
                    : i === step
                    ? "bg-brand-gold text-white"
                    : "bg-brand-green/10 text-brand-green/50"
                }`}
              >
                {i < step ? <Check size={16} /> : i + 1}
              </div>
              <span className={`text-xs hidden sm:inline ${i === step ? "text-brand-green font-medium" : "text-brand-green/50"}`}>
                {label}
              </span>
              {i < STEPS.length - 1 && <div className="w-4 sm:w-8 h-px bg-brand-green/20" />}
            </div>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -16 }}
            transition={{ duration: 0.3 }}
          >
            {/* Step 0: Location */}
            {step === 0 && (
              <div className="grid md:grid-cols-3 gap-5" data-testid="step-location">
                {LOCATIONS.map((loc) => (
                  <button
                    key={loc.id}
                    onClick={() => setLocation(loc)}
                    data-testid={`select-location-${loc.id}`}
                    className={`text-left bg-white rounded-2xl border p-6 transition-all ${
                      location?.id === loc.id ? "border-brand-gold ring-2 ring-brand-gold/30" : "border-brand-green/10 hover:border-brand-green/30"
                    }`}
                  >
                    <div className="text-xs text-brand-gold uppercase tracking-wider">{loc.flag} {loc.country}</div>
                    <h3 className="font-serif text-lg text-brand-green mt-2 flex items-center gap-2">
                      <MapPin size={16} className="text-brand-gold" /> {loc.name}
                    </h3>
                    <p className="text-brand-green/65 text-sm mt-2">{loc.address}</p>
                  </button>
                ))}
              </div>
            )}

            {/* Step 1: Category */}
            {step === 1 && (
              <div className="grid md:grid-cols-3 gap-5" data-testid="step-category">
                {CATEGORY_ORDER.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => { setCategory(cat); setTreatment(null); }}
                    data-testid={`select-category-${cat}`}
                    className={`text-left bg-white rounded-2xl border p-6 transition-all ${
                      category === cat ? "border-brand-gold ring-2 ring-brand-gold/30" : "border-brand-green/10 hover:border-brand-green/30"
                    }`}
                  >
                    <h3 className="font-serif text-lg text-brand-green">{cat}</h3>
                    <p className="text-brand-green/65 text-sm mt-2">{CATEGORY_META[cat]?.blurb}</p>
                  </button>
                ))}
              </div>
            )}

            {/* Step 2: Treatment */}
            {step === 2 && (
              <div data-testid="step-treatment">
                {loadingTreatments ? (
                  <div className="flex items-center justify-center py-16 text-brand-green/60" data-testid="treatments-loading">
                    <Loader2 className="animate-spin mr-2" size={20} /> Loading treatments...
                  </div>
                ) : treatmentsError ? (
                  <div className="text-center py-16" data-testid="treatments-error">
                    <AlertCircle className="mx-auto text-brand-gold mb-3" size={32} />
                    <p className="text-brand-green/75">We couldn't load treatments. Please check your connection and try again.</p>
                    <button
                      onClick={loadTreatments}
                      data-testid="retry-treatments-button"
                      className="mt-5 inline-flex items-center gap-2 bg-brand-green text-white rounded-full px-6 py-2.5 text-sm font-medium hover:bg-brand-greenDark transition-all"
                    >
                      <RotateCw size={16} /> Retry
                    </button>
                  </div>
                ) : categoryTreatments.length === 0 ? (
                  <div className="text-center py-16 text-brand-green/60" data-testid="treatments-empty">
                    No treatments available in this category yet. Please go back and choose another category.
                  </div>
                ) : (
                  <div className="grid sm:grid-cols-2 gap-4">
                    {categoryTreatments.map((t) => (
                      <button
                        key={t.id}
                        onClick={() => setTreatment(t)}
                        data-testid={`select-treatment-${t.id}`}
                        className={`text-left bg-white rounded-2xl border p-5 transition-all ${
                          treatment?.id === t.id ? "border-brand-gold ring-2 ring-brand-gold/30" : "border-brand-green/10 hover:border-brand-green/30"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <h3 className="font-serif text-lg text-brand-green">{t.name}</h3>
                          <span className="font-serif text-brand-gold text-lg whitespace-nowrap">{t.currency}{t.price}</span>
                        </div>
                        <p className="text-brand-green/60 text-sm mt-2">{t.description}</p>
                        <span className="mt-3 inline-flex items-center gap-1.5 text-brand-green/70 text-xs">
                          <Clock size={13} /> {t.duration_minutes} min
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Date & Time */}
            {step === 3 && (
              <div data-testid="step-datetime">
                <h3 className="font-serif text-lg text-brand-green flex items-center gap-2 mb-4">
                  <CalendarDays size={18} className="text-brand-gold" /> Select a date
                </h3>
                <div className="grid grid-cols-3 sm:grid-cols-7 gap-2">
                  {days.map((d) => {
                    const active = date && d.toDateString() === date.toDateString();
                    return (
                      <button
                        key={d.toISOString()}
                        onClick={() => setDate(d)}
                        data-testid={`select-date-${d.toISOString().slice(0, 10)}`}
                        className={`rounded-xl border py-3 text-center transition-all ${
                          active ? "bg-brand-green text-white border-brand-green" : "bg-white border-brand-green/10 hover:border-brand-green/30 text-brand-green"
                        }`}
                      >
                        <div className="text-[10px] uppercase opacity-70">{d.toLocaleDateString("en-GB", { weekday: "short" })}</div>
                        <div className="text-lg font-serif">{d.getDate()}</div>
                        <div className="text-[10px] opacity-70">{d.toLocaleDateString("en-GB", { month: "short" })}</div>
                      </button>
                    );
                  })}
                </div>

                {date && (
                  <>
                    <h3 className="font-serif text-lg text-brand-green flex items-center gap-2 mt-8 mb-4">
                      <Clock size={18} className="text-brand-gold" /> Select a time
                    </h3>
                    <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                      {slots.map((s) => (
                        <button
                          key={s}
                          onClick={() => setTime(s)}
                          data-testid={`select-time-${s}`}
                          className={`rounded-lg border py-2.5 text-sm transition-all ${
                            time === s ? "bg-brand-gold text-white border-brand-gold" : "bg-white border-brand-green/10 hover:border-brand-green/30 text-brand-green"
                          }`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Step 4: Details */}
            {step === 4 && (
              <div className="grid md:grid-cols-2 gap-8" data-testid="step-details">
                <div className="space-y-4">
                  <h3 className="font-serif text-lg text-brand-green flex items-center gap-2">
                    <User size={18} className="text-brand-gold" /> Your details
                  </h3>
                  <div>
                    <Label htmlFor="name">Full Name *</Label>
                    <Input id="name" data-testid="input-name" value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" placeholder="Jane Doe" />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input id="email" type="email" data-testid="input-email" value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" placeholder="jane@email.com" />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone *</Label>
                    <Input id="phone" data-testid="input-phone" value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" placeholder="+44 ..." />
                  </div>
                  <div>
                    <Label htmlFor="notes">Notes (optional)</Label>
                    <Textarea id="notes" data-testid="input-notes" value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" placeholder="Anything we should know?" />
                  </div>
                </div>

                {/* Summary */}
                <div className="bg-white rounded-2xl border border-brand-green/10 p-6 h-fit">
                  <h3 className="font-serif text-lg text-brand-green mb-4">Booking Summary</h3>
                  <dl className="space-y-3 text-sm">
                    <Row label="Name" value={form.name} />
                    <Row label="Email" value={form.email} />
                    <Row label="Location" value={`${location?.name}`} />
                    <Row label="Treatment" value={treatment?.name} />
                    <Row label="Duration" value={`${treatment?.duration_minutes} min`} />
                    <Row label="Date" value={date?.toDateString()} />
                    <Row label="Time" value={time} />
                    <div className="flex items-center justify-between pt-3 border-t border-brand-green/10">
                      <dt className="text-brand-green/70">Price</dt>
                      <dd className="font-serif text-brand-gold text-xl">{treatment?.currency}{treatment?.price}</dd>
                    </div>
                  </dl>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Nav buttons */}
        <div className="flex items-center justify-between mt-12">
          <button
            onClick={step === 0 ? () => navigate("/") : goBack}
            data-testid="booking-back-button"
            className="flex items-center gap-2 text-brand-green/70 hover:text-brand-green transition-colors"
          >
            <ArrowLeft size={16} /> {step === 0 ? "Cancel" : "Back"}
          </button>

          {step < STEPS.length - 1 ? (
            <button
              onClick={goNext}
              disabled={!canNext()}
              data-testid="booking-next-button"
              className="flex items-center gap-2 bg-brand-green text-white rounded-full px-8 py-3 font-medium hover:bg-brand-greenDark transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Continue <ArrowRight size={16} />
            </button>
          ) : (
            <button
              onClick={submit}
              disabled={submitting}
              data-testid="booking-submit-button"
              className="flex items-center gap-2 bg-brand-gold text-white rounded-full px-8 py-3 font-medium hover:bg-[#a2762f] transition-all disabled:opacity-50"
            >
              {submitting ? "Submitting..." : "Confirm Booking"} <Check size={16} />
            </button>
          )}
        </div>
      </div>
    </PublicLayout>
  );
}

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between">
    <dt className="text-brand-green/70">{label}</dt>
    <dd className="text-brand-green font-medium text-right">{value || "—"}</dd>
  </div>
);
